import { useEffect, useRef, useState } from 'react';
import { createSignaling, newSessionId } from './firebaseSignaling';
import { createDesktopHost } from './WebRTCManager';

// Connection states surfaced to the UI
export const STATUS = {
  IDLE: 'idle',
  WAITING: 'waiting',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  RECONNECTING: 'reconnecting',
  DISCONNECTED: 'disconnected',
  TIMED_OUT: 'timed_out',
  FAILED: 'failed',
};

// 2-minute pairing window. Keep it generous so the user has time to
// pick up their phone, scan the QR, grant camera permission, etc. If the
// peer connection still hasn't progressed past "new" by then, we surface
// the "Host not found on this network" error.
const PAIRING_TIMEOUT_MS = 120000;

// TEMP — testing override. Set to a non-empty string to force the QR to
// encode this exact origin instead of window.location.origin. Useful when
// the dev desktop runs on http://localhost but you want the phone to hit
// a known LAN IP. Set to '' to fall back to the live origin.
const PAIRING_ORIGIN_OVERRIDE = null;

/**
 * Hook that manages a desktop-side mobile-camera session.
 *
 * Returns:
 *   sessionId           — short id encoded into the QR
 *   joinUrl             — full URL the phone scans
 *   status              — STATUS enum
 *   stream              — incoming MediaStream (null until connected)
 *   start()             — open a session + show QR
 *   sendControl(cmd)    — relay phone-side commands (e.g. switchCamera)
 *   disconnect()        — tear down peer + signaling
 */
export const useMobileCameraSession = () => {
  const [sessionId, setSessionId] = useState(null);
  const [status, setStatus] = useState(STATUS.IDLE);
  const [stream, setStream] = useState(null);
  // Latest command sent from the phone (start / finish / skip / etc.).
  // GiftRecorder reacts via useEffect and routes to its existing handlers.
  const [lastPhoneCommand, setLastPhoneCommand] = useState(null);

  const signalingRef = useRef(null);
  const hostRef = useRef(null);
  const timeoutRef = useRef(null);
  const reachedConnectingRef = useRef(false);

  const teardown = async () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (hostRef.current) {
      try { hostRef.current.stop(); } catch (e) { /* noop */ }
      hostRef.current = null;
    }
    if (signalingRef.current) {
      try { await signalingRef.current.teardown(); } catch (e) { /* noop */ }
      signalingRef.current = null;
    }
    setStream(null);
    reachedConnectingRef.current = false;
  };

  const disconnect = async () => {
    await teardown();
    setSessionId(null);
    setStatus(STATUS.IDLE);
  };

  const start = async () => {
    await teardown();
    reachedConnectingRef.current = false;

    const id = newSessionId();
    setSessionId(id);
    setStatus(STATUS.WAITING);

    const signaling = createSignaling(id);
    signalingRef.current = signaling;
    await signaling.init();

    const host = createDesktopHost({
      signaling,
      onTrack: (incoming) => {
        setStream(incoming);
      },
      onConnectionStateChange: (state) => {
        if (state === 'connecting' || state === 'new' || state === 'checking') {
          reachedConnectingRef.current = true;
          setStatus((s) => (s === STATUS.CONNECTED ? s : STATUS.CONNECTING));
        } else if (state === 'connected') {
          setStatus(STATUS.CONNECTED);
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
          try { signaling.setStatus('connected'); } catch (e) { /* noop */ }
        } else if (state === 'disconnected') {
          setStatus(STATUS.RECONNECTING);
        } else if (state === 'failed' || state === 'closed') {
          setStatus(STATUS.DISCONNECTED);
        }
      },
    });
    hostRef.current = host;
    await host.start();

    // Listen for commands sent from the phone (Start / Skip / Finish, etc.)
    signaling.onMobileControl((msg) => {
      if (msg && msg.command) setLastPhoneCommand({ ...msg });
    });

    // Pairing timeout — if we never even got to "connecting" by the deadline,
    // it almost always means the two devices aren't on the same network.
    timeoutRef.current = setTimeout(() => {
      // Still waiting — phone never called in
      if (!reachedConnectingRef.current) {
        setStatus(STATUS.TIMED_OUT);
      }
    }, PAIRING_TIMEOUT_MS);
  };

  const sendControl = async (command) => {
    if (signalingRef.current) {
      try { await signalingRef.current.sendControl(command); } catch (e) { /* noop */ }
    }
  };

  // Cleanup on unmount
  useEffect(() => () => { teardown(); }, []);

  // Build the URL the phone navigates to. In normal operation this mirrors
  // the desktop's origin (preserving HTTPS in staging/prod). For local LAN
  // testing the override above lets the QR point at a fixed IP.
  const baseOrigin = PAIRING_ORIGIN_OVERRIDE || window.location.origin;
  const joinUrl = sessionId
    ? `${baseOrigin}/mobile-camera/${sessionId}`
    : '';

  // Optional helper for the desktop side to broadcast the recorder's phase
  // to the phone so its UI can highlight the right button.
  const sendPhase = async (phasePayload) => {
    if (!signalingRef.current) return;
    try { await signalingRef.current.sendControl(phasePayload); } catch (e) { /* noop */ }
  };

  return {
    sessionId,
    joinUrl,
    status,
    stream,
    start,
    sendControl,
    sendPhase,
    lastPhoneCommand,
    disconnect,
  };
};
